import styled from '@emotion/styled';
import {
  Paper,
  RootRef,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { List } from 'immutable';
import { createElement, FC, useState } from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';

import {
  DetailPerformanceValue,
  PerformanceReport,
  PeriodicDetailPerformanceWithAllFieldsFragment,
} from '../../../../generated/graphql';
import { getLatestDeliverable } from '../../../../graphql/creation';
import useScrollableBoxShadow, { BoxShadowProps } from '../../../../hooks/useScrollableBoxShadow';
import formatPerformanceValue from '../../../../utility/formatPerformanceValue';
import { SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import mq, { breakpoint } from '../../../styleUtility/mediaquery';
import ImageScreen from '../../primitive/ImageScreen';

interface Props {
  className?: string;
  report: PerformanceReport;
  periodicPerformance: PeriodicDetailPerformanceWithAllFieldsFragment;
}

type TableOrder = { key?: string; direction: 'asc' | 'desc' };

const DetailPerformanceTable: FC<Props & InjectedIntlProps> = ({ report, periodicPerformance, className, intl }) => {
  const [order, setOrder] = useState<TableOrder>({ key: undefined, direction: 'desc' });
  const { tableRef, scrollContentRef, headerRef, scrolling, handleScroll, boxShadowSize } = useScrollableBoxShadow();
  const isSmartPhoneScreenWidth = useMediaQuery(`(max-width:${breakpoint.sp}px)`);

  const handleSort = (key: string | null) => {
    if (key === null) {
      setOrder({ key: undefined, direction: 'desc' });
      return;
    }
    if (order.key === key) {
      setOrder({ ...order, direction: order.direction === 'desc' ? 'asc' : 'desc' });
      return;
    }
    setOrder({ key, direction: 'desc' });
  };

  const headerCells = report.metrics.map((metric) => {
    return (
      <SizedTableCell key={`headerCell_${metric}`} align="right">
        <TableSortLabel active={order.key === metric} direction={order.direction} onClick={() => handleSort(metric)}>
          <Typography variant="caption" noWrap>
            <FormattedMessage id={`performance_report_metric.${metric.toLowerCase()}`} />
          </Typography>
        </TableSortLabel>
      </SizedTableCell>
    );
  });

  const sortRows = <T extends { values: Array<Pick<DetailPerformanceValue, 'metric' | 'actualValue'>> }>(
    creatives: T[],
    order: TableOrder,
  ) => {
    if (!order.key) {
      return creatives;
    }
    return List(creatives)
      .sort((a, b) => {
        const aValue = a.values.find((v) => v.metric === order.key)!.actualValue;
        const bValue = b.values.find((v) => v.metric === order.key)!.actualValue;
        if (aValue === null && bValue === null) {
          return 0;
        }
        const diff = (aValue ? aValue.value ?? 0 : 0) - (bValue ? bValue.value ?? 0 : 0);
        if (order.direction === 'asc') {
          return diff;
        } else {
          return -diff;
        }
      })
      .toArray();
  };

  const body = sortRows(periodicPerformance.creatives, order).map((creative, index) => {
    const bodyCells = report.metrics.map((metric) => {
      const value = creative.values.find((value) => value.metric === metric)!;
      return (
        <SizedTableCell key={`bodyCell_${metric}`} align="right">
          {value.actualValue ? formatPerformanceValue(intl, value.actualValue) : '-'}
        </SizedTableCell>
      );
    });

    const latestDeliverable = creative.creation && getLatestDeliverable(creative.creation);
    return (
      <TableRow key={`creativeRow_${index}`}>
        <SizedCreativeCell component="th">
          {creative.creation ? (
            <KaizenAdCreative>
              {creative.thumbnailImageUrl ? (
                <CreativeThumbnail src={creative.thumbnailImageUrl} />
              ) : creative.creation && latestDeliverable && latestDeliverable.creative.file.thumbnailUrl ? (
                <CreativeThumbnail src={latestDeliverable.creative.file.thumbnailUrl} />
              ) : (
                <DummyThumbnail src={'/assets/dummy_thumbnail.svg'} />
              )}
              <CreativeNames>
                <CreativeName color="textPrimary">ID: {creative.creation.id}</CreativeName>
                <CreativeName title={creative.adName}>{creative.adName}</CreativeName>
              </CreativeNames>
            </KaizenAdCreative>
          ) : (
            <OtherCreative>
              {!creative.thumbnailImageUrl ? (
                <DummyThumbnail src={'/assets/dummy_thumbnail.svg'} />
              ) : (
                <CreativeThumbnail src={creative.thumbnailImageUrl} />
              )}
              <CreativeNames>
                <CreativeName title={creative.adName} color="textPrimary">
                  {creative.adName}
                </CreativeName>
              </CreativeNames>
            </OtherCreative>
          )}
        </SizedCreativeCell>
        {bodyCells}
      </TableRow>
    );
  });

  return (
    <Root className={className}>
      {scrolling && !isSmartPhoneScreenWidth && <BoxShadow width={boxShadowSize.width} height={boxShadowSize.height} />}
      <PerformanceTableWrapper ref={tableRef} onScroll={handleScroll}>
        <Table style={{ borderCollapse: 'separate' }}>
          <TableHead>
            <HeaderRow>
              <RootRef rootRef={headerRef}>
                <SizedCreativeCell align="center">
                  <TableSortLabel onClick={() => handleSort(null)}>
                    <FormattedMessage id="periodic_detail_performance.columns.creative" />
                  </TableSortLabel>
                </SizedCreativeCell>
              </RootRef>
              {headerCells}
            </HeaderRow>
          </TableHead>
          <RootRef rootRef={scrollContentRef}>
            <TableBody>{body}</TableBody>
          </RootRef>
        </Table>
      </PerformanceTableWrapper>
    </Root>
  );
};

const Root = styled(Paper)({
  ...gridLayoutContainer({
    areas: [['table']],
    columns: ['auto'],
    rows: ['auto'],
    alignItems: 'center',
  }),
  position: 'relative',
});

const BoxShadow = styled('div')<BoxShadowProps>((props: BoxShadowProps) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: props.width,
  height: props.height,
  zIndex: 10,
  boxShadow: '3px -0px 5px -2px rgba(0,0,0,0.1)',
}));

const PerformanceTableWrapper = styled('div')({
  ...gridLayoutItem('table'),
  overflowX: 'auto',
  position: 'relative',
  zIndex: 1,
});

const creativeStyle = {
  ...gridLayoutContainer({
    areas: [['thumbnail', 'names']],
    rows: ['auto'],
    columns: ['auto', '230px'],
    alignItems: 'center',
  }),
  height: 64,
  paddingLeft: SPACING_UNIT,
};

const KaizenAdCreative = styled('div')({
  ...creativeStyle,
});

const OtherCreative = styled('div')({
  ...creativeStyle,
});

const CreativeThumbnail = styled(ImageScreen)({
  ...gridLayoutItem('thumbnail'),
  minWidth: 60,
  maxWidth: 60,
  minHeight: 60,
  maxHeight: 60,
  marginRight: SPACING_UNIT,
});

const DummyThumbnail = styled(ImageScreen)({
  ...gridLayoutItem('thumbnail'),
  background: '#fff',
  minWidth: 60,
  maxWidth: 60,
  height: 60,
  marginRight: SPACING_UNIT,
});

const CreativeNames = styled('div')({
  ...gridLayoutItem('names'),
  ...flexibleLayoutContainer({
    direction: 'column',
  }),
});

const CreativeName = styled(Typography)({});
CreativeName.defaultProps = { variant: 'caption', noWrap: true };

const HeaderRow = styled(TableRow)({
  height: 40,
});

const SizedTableCell = styled(TableCell)({
  padding: `0 ${SPACING_UNIT}px`,
});

const SizedCreativeCell = styled(TableCell)({
  padding: `0 ${SPACING_UNIT}px`,
  position: 'sticky',
  left: 0,
  zIndex: 2,
  background: '#fff',

  [mq('sp')]: {
    position: 'initial',
  },
});

export default injectIntl(DetailPerformanceTable);
